import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Observable, lastValueFrom, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import {
	Integration,
	INTEGRATION_TYPE,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	RestService,
	OAuthClients,
} from 'cargo-chief-commons';

import { UrlService } from '../url/url.service';
import { Cargochief } from '../cargochief/cargochief.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { SegmentWrapperService } from '../segmentWrapper/segment-wrapper.service';
import { UtilService } from '../util/util.service';
import { ProductsService } from '@service/products/products.service';

import { ACCOUNT_TYPE, CARGOCHIEF_KEYS } from '@domain/constants/app-variables';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { ConfirmationDialogComponent } from '../../common/confirmation-dialog/confirmation-dialog.component';
import { environment } from '@environment/environment';
import { CarrierVettingService } from '@service/carrier-vetting/carrier-vetting.service';
import { RMISService } from '@service/rmis/rmis.service';
import { CarrierService } from '@service/carrier/carrier.service';

@Injectable({
	providedIn: 'root',
})
export class IntegrationService {
	constructor(
		public productsService: ProductsService,
		private urlService: UrlService,
		private restService: RestService,
		private cargochief: Cargochief,
		private segmentWrapper: SegmentWrapperService,
		private utilService: UtilService,
		private dialog: MatDialog,
		private carrierVettingService: CarrierVettingService,
		private rmisService: RMISService,
		private carrierService: CarrierService
	) {
		if (this.cargochief.getLoginSetupUser() && this.cargochief.USER_ACCOUNT_TYPE !== ACCOUNT_TYPE.CARRIER) {
			this.initialiseIntegrationsType();
		}
	}

	async initialiseIntegrationsType() {
		try {
			const integrations: Integration[] = await lastValueFrom<Integration[]>(
				this.getIntegrations()
			);
			this.cargochief.setItem({ [CARGOCHIEF_KEYS.INTEGRATIONS_TYPE]: integrations });
		} catch (error) {
			console.error('Failed to initialize integrations type:', error);
			// Set empty array as fallback
			this.cargochief.setItem({ [CARGOCHIEF_KEYS.INTEGRATIONS_TYPE]: [] });
		}
	}

	getIntegrations(integrationType: INTEGRATION_TYPE = null): Observable<Integration[]> {
		const url: string = this.urlService.listIntegrationsUrl(integrationType);
		return this.restService.GET(url);
	}

	getApiClients(): Observable<OAuthClients[]> {
		const url: string = this.urlService.getAccountAuthorizedApiClients();
		return this.restService.GET(url);
	}

	updateIntegration(url: string, integration: Integration): Observable<Integration> {
		// Avoid sending the whole account object as it is not needed
		integration['account'] = null;
		let payload: any = Object.assign({}, integration);
		return this.restService.POST(url, payload);
	}

	disconnectIntegration(url: string, integration: Integration): Observable<Integration> {
		let payload: any = Object.assign({}, integration);
		return this.restService.DELETE(url, payload);
	}

	getMyCarrierPacketsToken() {
		if (
			environment.INTEGRATIONS &&
			this.productsService.INTEGRATIONS &&
			!this.cargochief.ACTIVE_INTEGRATIONS
		) {
			this.fetchAndStoreActiveIntegrations();
		}
	}

	showMyCarrierEmailConfirmation(
		businessName: string,
		dot: string,
		carrierId: string,
		payload: any,
		capacitySearch: any = null,
		truckLaneSearchId: string = null,
		integrationType: INTEGRATION_TYPE = null
	) {
		this.fireCarrierOnboardingEvent(SegmentEventsConstants.user_clicked_onboard_carrier, payload);
		const user = this.cargochief.getLoginSetupUser();
		let myCarrierPackets: any = this.cargochief.MY_CARRIER_PACKETS,
			integrationsUrl: string = this.getIntegrationInviteLink(),
			emailContent: string = '',
			mcpIntegrationId!: string;
		if (integrationType !== INTEGRATION_TYPE.RMIS) {
			if (IS_NOT_EMPTY(myCarrierPackets?.data)) {
				mcpIntegrationId = myCarrierPackets.id;
				myCarrierPackets = JSON.parse(myCarrierPackets.data);
			}

			if (IS_NOT_NULL_OR_UNDEFINED(myCarrierPackets?.intelliviteLink)) {
				const baseLink = myCarrierPackets.intelliviteLink.replace(/\/+$/, ''); // Remove trailing slashes
				integrationsUrl = IS_NOT_EMPTY(dot) ? `${baseLink}/${dot}` : baseLink;
			}
			if (IS_NOT_EMPTY(user.onboardingLink)) {
				integrationsUrl = user.onboardingLink;
			}

			let integrationDisplayString = this.getIntegrationDisplayName(integrationType);
			if (IS_NOT_NULL_OR_UNDEFINED(capacitySearch)) {
				emailContent += `
				<p>
					We are working ${capacitySearch.origin.city}, ${capacitySearch.origin.state} to ${capacitySearch.destination.city}, ${capacitySearch.destination.state} and it
					looks like you would be a great match. We would like to get you setup in our network. ${integrationDisplayString}:
				</p>
			`;
			} else {
				emailContent += `<p>${integrationDisplayString}:</p>`;
			}
		} else {
			emailContent += 'We would like to get you setup in our network.';
		}

		const integrationTypeDisplay =
			integrationType === INTEGRATION_TYPE.MY_CARRIER_PACKETS ? 'MyCarrierPortal' : integrationType;
		const title = `Invite ${businessName} to join your network using ${integrationTypeDisplay}.`;
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '700px',
			data: {
				title,
				body: `
					<div class="primary-text">
						<p>Hey &#60;carrier name&#62;,</p>
						${emailContent}
						${
							integrationType != INTEGRATION_TYPE.RMIS
								? `<p>
									<a class="link" href="${integrationsUrl}" target="_blank" >
										${integrationsUrl}
									</a>
								</p>`
								: ''
						}
						<br />
						<p>
							Thanks,
							<br />
							<br />
							${this.cargochief.USER_NAME}
							<br />
							${this.cargochief.USER_EMAIL} | ${this.cargochief.getUser()?.phoneNumber}
						</p>
						<br />
						<br />
					</div>
					`,
				cancelButtonText: 'Cancel',
				ConfirmButtonText: 'Confirm',
				headerClass: 'primary-text',
				confirmationText:
					'Please click confirm and we will email the carrier and get them setup in your network.',
				confirmationTextClass: 'primary-text',
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			const isRMISEnabled = integrationType == INTEGRATION_TYPE.RMIS;
			if (IS_NOT_NULL_OR_UNDEFINED(result) && result) {
				if (isRMISEnabled) {
					this.rmisService.inviteCarrier(
						carrierId,
						this.cargochief.ACCOUNT_ID,
						this.cargochief.USER_ID
					);
				} else {
					this.sendOnboardCarrierEmail(
						mcpIntegrationId,
						dot,
						carrierId,
						payload,
						truckLaneSearchId
					);
				}
			}
		});
	}

	sendOnboardCarrierEmail(
		id: string,
		dot: string,
		carrierId: string,
		payload,
		truckLaneSearchId: string
	) {
		const url = this.urlService.onboardCarriersURL(id, dot, carrierId, truckLaneSearchId);
		this.restService.POST(url, {}).subscribe(
			(result) => {
				this.utilService.showSnackbar(result.message, SNACKBAR.SUCCESS, SNACKBAR.ACTION);
				this.fireCarrierOnboardingEvent(
					SegmentEventsConstants.user_confirmed_onboard_carrier,
					payload
				);
				this.carrierVettingService.carrierInvitationSent(carrierId);
			},
			(error) => {
				this.utilService.showSnackbar(
					'Unable to send invitation email now.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
			}
		);
	}

	// CN-8777: Bulk onboard — send the vetting-integration onboarding invite to many carriers
	// from a single confirmation. Reuses the per-carrier send (POST /vetting/onboard) fired in
	// parallel with one aggregate result toast. RMIS accounts loop rmisService.inviteCarrier,
	// the same per-carrier path the single flow uses. `carriers` should already be filtered to
	// onboard-eligible (each needs { id, dot, businessName }).
	bulkOnboardCarriers(carriers: any[], payload: any = {}): void {
		if (!carriers || carriers.length === 0) {
			return;
		}

		const integrationType = this.getEnabledCarrierVettingIntegration();
		const isRMIS = integrationType === INTEGRATION_TYPE.RMIS;

		let mcpIntegrationId: string = null;
		const myCarrierPackets: any = this.cargochief.MY_CARRIER_PACKETS;
		if (IS_NOT_EMPTY(myCarrierPackets?.data)) {
			mcpIntegrationId = myCarrierPackets.id;
		}

		// Carrier business names are import-sourced; escape before interpolating into the
		// dialog HTML body (ConfirmationDialogComponent renders body as HTML).
		const escapeHtml = (value: string): string =>
			(value || '')
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/"/g, '&quot;')
				.replace(/'/g, '&#39;');
		const names = carriers.map((c) => escapeHtml(c.businessName)).filter((n) => IS_NOT_EMPTY(n));
		const namePreview =
			names.slice(0, 8).join(', ') + (names.length > 8 ? `, +${names.length - 8} more` : '');
		const plural = carriers.length > 1 ? 's' : '';

		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '700px',
			data: {
				title: `Onboard ${carriers.length} carrier${plural}`,
				body: `
					<div class="primary-text">
						<p>We will email an onboarding invitation to the following ${carriers.length} carrier${plural}:</p>
						<p>${namePreview}</p>
					</div>
				`,
				cancelButtonText: 'Cancel',
				ConfirmButtonText: 'Send Invites',
				headerClass: 'primary-text',
				confirmationText:
					'Please click confirm and we will email these carriers and get them set up in your network.',
				confirmationTextClass: 'primary-text',
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (!result) {
				return;
			}

			this.fireCarrierOnboardingEvent(
				SegmentEventsConstants.user_confirmed_onboard_carrier,
				payload
			);

			if (isRMIS) {
				carriers.forEach((carrier) => {
					this.rmisService.inviteCarrier(
						carrier.id,
						this.cargochief.ACCOUNT_ID,
						this.cargochief.USER_ID
					);
					this.carrierVettingService.carrierInvitationSent(carrier.id);
				});
				this.utilService.showSnackbar(
					`Onboarding invitations sent to ${carriers.length} carrier${plural}.`,
					SNACKBAR.SUCCESS,
					SNACKBAR.ACTION
				);
				return;
			}

			const calls = carriers.map((carrier) =>
				this.restService
					.POST(
						this.urlService.onboardCarriersURL(mcpIntegrationId, carrier.dot, carrier.id, null),
						{}
					)
					.pipe(
						map(() => ({ carrier, ok: true })),
						catchError(() => of({ carrier, ok: false }))
					)
			);

			forkJoin(calls).subscribe((results: Array<{ carrier: any; ok: boolean }>) => {
				const succeeded = results.filter((r) => r.ok);
				succeeded.forEach((r) => this.carrierVettingService.carrierInvitationSent(r.carrier.id));
				const failed = results.length - succeeded.length;
				const message = failed
					? `Onboarding invitations sent to ${succeeded.length} of ${results.length} carriers (${failed} failed).`
					: `Onboarding invitations sent to ${succeeded.length} carrier${succeeded.length > 1 ? 's' : ''}.`;
				this.utilService.showSnackbar(
					message,
					failed ? SNACKBAR.ERROR : SNACKBAR.SUCCESS,
					SNACKBAR.ACTION
				);
			});
		});
	}

	fireCarrierOnboardingEvent(eventName: string, payload: any) {
		this.segmentWrapper.fireSegmentEvent(eventName, payload);
	}

	sortIntegrations(integrations: Integration[]) {
		let enabledIntegrations = integrations.filter((integration) => integration.enabled);
		let disabledIntegrations = integrations.filter((integration) => !integration.enabled);
		enabledIntegrations = enabledIntegrations.sort((a, b) => {
			if (a.integrationType < b.integrationType) {
				return -1;
			}
			if (a.integrationType > b.integrationType) {
				return 1;
			}
			return 0;
		});

		let disabledCustomIntegration = disabledIntegrations.filter(
			(integration) => integration.integrationType === INTEGRATION_TYPE.CUSTOM
		);
		if (disabledCustomIntegration) {
			disabledIntegrations = disabledIntegrations.filter(
				(integration) => integration.integrationType !== INTEGRATION_TYPE.CUSTOM
			);
		}

		disabledIntegrations = disabledIntegrations.sort((a, b) => {
			if (a.integrationType < b.integrationType) {
				return -1;
			}
			if (a.integrationType > b.integrationType) {
				return 1;
			}
			return 0;
		});
		// Custom integration type shoud always be at the bottom of the disabled integrations.
		disabledIntegrations = disabledIntegrations.concat(disabledCustomIntegration);
		return enabledIntegrations.concat(disabledIntegrations);
	}

	activeIntegrationType(integrations: Integration[]): INTEGRATION_TYPE {
		let integrationType = null;
		const activeIntegration = integrations.find((integration) => integration.enabled);
		if (IS_NOT_NULL_OR_UNDEFINED(activeIntegration)) {
			integrationType = activeIntegration.integrationType;
		}
		return integrationType;
	}

	isCarrierVettingIntegrationEnabled(): boolean {
		const activeIntegrations = this.cargochief.getActiveIntegrations();
		let hasVettingIntegration = false;
		activeIntegrations?.forEach((activeIntegration) => {
			if (this.cargochief.VETTING_INTEGRATION_TYPES?.includes(activeIntegration)) {
				hasVettingIntegration = true;
			}
		});
		return hasVettingIntegration;
	}

	getEnabledCarrierVettingIntegration(): INTEGRATION_TYPE {
		const activeIntegrations = this.cargochief.getActiveIntegrations();
		let activeVettingIntegration = null;
		activeIntegrations?.forEach((activeIntegration) => {
			if (this.cargochief.VETTING_INTEGRATION_TYPES?.includes(activeIntegration)) {
				activeVettingIntegration = INTEGRATION_TYPE[activeIntegration];
			}
		});
		return activeVettingIntegration;
	}

	getIntegrationDisplayName(integrationType): string {
		let integrationDisplayName = 'Please click the link below to complete our setup';
		switch (integrationType) {
			case INTEGRATION_TYPE.MY_CARRIER_PACKETS:
				integrationDisplayName =
					'Please click the link below to complete our onboarding through MyCarrierPortal';
				break;
			case INTEGRATION_TYPE.CUSTOM:
				break;
			default:
				integrationDisplayName = integrationType
					? `Please click the link below to complete our onboarding through ${String(integrationType).charAt(0).toUpperCase() + String(integrationType).slice(1).toLowerCase()}`
					: integrationDisplayName;
				break;
		}
		return integrationDisplayName;
	}

	isIntegrationUrlAvailable(): boolean {
		let result = false,
			integrationsUrl: string = null;
		const user = this.cargochief.getLoginSetupUser();
		if (user?.onboardingLink) {
			return true;
		} else {
			let activeVettingIntegration: INTEGRATION_TYPE = this.getEnabledCarrierVettingIntegration();
			if (activeVettingIntegration) {
				let activeIntegration = this.cargochief.getIntegrationData(activeVettingIntegration);
				if (IS_NOT_EMPTY(activeIntegration?.data)) {
					try {
						activeIntegration = JSON.parse(activeIntegration.data);
						if (activeIntegration) {
							const inviteLinkeys = ['intelliviteLink', 'inviteLink', 'url'];
							for (let keyIndex = 0; keyIndex < inviteLinkeys.length; keyIndex++) {
								if (activeIntegration[inviteLinkeys[keyIndex]]) {
									integrationsUrl = activeIntegration[inviteLinkeys[keyIndex]];
									break;
								}
							}
						}
					} catch (parseError) {
						console.error('Error parsing integration data:', parseError);
					}
				}
			}
		}
		result = IS_NOT_EMPTY(integrationsUrl);
		return result;
	}

	getIntegrationInviteLink(): string {
		let integrationsUrl: string = null;
		const user = this.cargochief.getLoginSetupUser();
		if (user?.onboardingLink) {
			integrationsUrl = user?.onboardingLink;
			return integrationsUrl;
		} else {
			let activeVettingIntegration: INTEGRATION_TYPE = this.getEnabledCarrierVettingIntegration();
			if (activeVettingIntegration) {
				let activeIntegration = this.cargochief.getIntegrationData(activeVettingIntegration);
				if (IS_NOT_EMPTY(activeIntegration?.data)) {
					try {
						activeIntegration = JSON.parse(activeIntegration.data);
						if (activeIntegration) {
							const inviteLinkeys = ['intelliviteLink', 'inviteLink', 'url'];
							for (let keyIndex = 0; keyIndex < inviteLinkeys.length; keyIndex++) {
								if (activeIntegration[inviteLinkeys[keyIndex]]) {
									integrationsUrl = activeIntegration[inviteLinkeys[keyIndex]];
									break;
								}
							}
						}
					} catch (parseError) {
						console.error('Error parsing integration data:', parseError);
					}
				}
			}
		}
		return integrationsUrl;
	}

	isIntegrationEnabled(integrationType: INTEGRATION_TYPE): boolean {
		const activeIntegrations = this.cargochief.getActiveIntegrations();
		return (
			IS_NOT_NULL_OR_UNDEFINED(activeIntegrations) && activeIntegrations.includes(integrationType)
		);
	}

	fetchAndStoreActiveIntegrations(): void {
		const url: string = this.urlService.integrationsUrl();
		this.restService.GET(url).subscribe(
			(result: any) => {
				this.cargochief.setItem({ [CARGOCHIEF_KEYS.ACTIVE_INTEGRATIONS]: result.data });
			},
			(error) => {
				console.error('Unable to get active integrations.', error);
			}
		);
	}

	handleOnboarding(event: Event, availableLoad: any) {
		event.stopPropagation();
		const integrationType = availableLoad?.broker_vetting_integration_type;
		const brokerOnboardLink = availableLoad?.broker_onboard_link;
		const carrierId = this.cargochief.getCarrierId();
		const quote = availableLoad?.quoteDTO;
		if (integrationType === INTEGRATION_TYPE.RMIS) {
			if (!quote?.accountId || !quote?.customerId) {
				console.error(
					'Missing required quote data for RMIS onboarding in available load: %o',
					availableLoad
				);
				this.utilService.showSnackbar(
					'Unable to initiate onboarding. Please contact cargochief support.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
				return;
			}
			// Notify C4 to onboard carrier on behalf of the broker
			this.rmisService.inviteCarrier(
				carrierId,
				quote.accountId,
				quote.customerId,
				false,
				availableLoad
			);
		} else {
			if (!brokerOnboardLink) {
				console.error('Missing broker onboarding link in available load: %o', availableLoad);
				this.utilService.showSnackbar(
					'Unable to initiate onboarding. Please contact cargochief support.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
				return;
			}
			window.open(brokerOnboardLink, '_blank');
			// Notify C4 to send notification to broker that a carrier has started onboarding
			if (!quote?.accountId || !quote?.customerId) {
				console.error(
					'Missing required data to notify broker about carrier onboarding for available load: %o',
					availableLoad
				);
				return;
			}
			const integrationTypeString =
				integrationType == INTEGRATION_TYPE.MY_CARRIER_PACKETS
					? 'MyCarrierPortal'
					: integrationType == INTEGRATION_TYPE.HIGHWAY
						? 'Highway'
						: '';
			this.carrierService.carrierRequestedOnboarding(
				carrierId,
				quote.accountId,
				quote.customerId,
				integrationTypeString,
				availableLoad
			);
		}
	}
}
